import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, useToast, PhoneInput } from '@caruso/ui';
import { venueReservationsService } from '@/services/venue-reservations.service';
import { venuesService } from '@/services/venues.service';
import { clientsService } from '@/services/clients.service';
import { ArrowLeft, Save, DollarSign, Calendar, User, Users, Plus, X } from 'lucide-react';
import { CreateReservationDto, PricingType, CreateGuestDto } from '@caruso/types';
import { cn } from '@caruso/ui';
import 'react-phone-number-input/style.css';

export function VenueReservationFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const isEdit = !!id;

  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [guests, setGuests] = useState<CreateGuestDto[]>([]);
  const [priceCalculation, setPriceCalculation] = useState<any>(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  const { register, handleSubmit, watch, setValue } = useForm<CreateReservationDto>();

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const discount = watch('discount');
  const customerPhone = watch('customerPhone');

  // Load venues
  const { data: venues } = useQuery({
    queryKey: ['venues-active'],
    queryFn: () => venuesService.getAll(true),
  });

  // Load selected venue details
  const { data: selectedVenue } = useQuery({
    queryKey: ['venue', selectedVenueId],
    queryFn: () => venuesService.getById(selectedVenueId),
    enabled: !!selectedVenueId,
  });

  // Search clients
  const { data: clientsData } = useQuery({
    queryKey: ['clients-search', clientSearch],
    queryFn: () => clientsService.getAll({ search: clientSearch, page: 1, pageSize: 10 }),
    enabled: clientSearch.length > 2,
  });

  // Calculate price whenever dates change
  useEffect(() => {
    if (selectedVenueId && startDate && endDate) {
      calculatePrice();
    }
  }, [selectedVenueId, startDate, endDate, startTime, endTime, discount]);

  const calculatePrice = async () => {
    if (!selectedVenueId || !startDate || !endDate) return;

    setCalculatingPrice(true);
    try {
      const result = await venuesService.calculatePrice(selectedVenueId, {
        startDate,
        endDate,
        startTime,
        endTime,
        discount: discount || 0,
      });
      setPriceCalculation(result);
    } catch (error: any) {
      toast({
        title: 'Erro ao calcular preço',
        description: error.response?.data?.message,
        variant: 'destructive',
      });
    } finally {
      setCalculatingPrice(false);
    }
  };

  const mutation = useMutation({
    mutationFn: async (data: CreateReservationDto) => {
      const payload = { ...data, guests };
      return venueReservationsService.create(payload);
    },
    onSuccess: () => {
      toast({ title: 'Reserva criada com sucesso!', variant: 'success' });
      navigate('/app/venue-reservations');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar reserva',
        description: error.response?.data?.message,
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: CreateReservationDto) => {
    if (!selectedVenueId) {
      toast({ title: 'Selecione um ambiente', variant: 'destructive' });
      return;
    }
    mutation.mutate({ ...data, venueId: selectedVenueId });
  };

  const addGuest = () => {
    setGuests([...guests, { name: '', email: '', phone: '', document: '' }]);
  };

  const removeGuest = (index: number) => {
    setGuests(guests.filter((_, i) => i !== index));
  };

  const updateGuest = (index: number, field: keyof CreateGuestDto, value: string) => {
    const updated = [...guests];
    updated[index] = { ...updated[index], [field]: value };
    setGuests(updated);
  };

  const selectClient = (client: any) => {
    setValue('customerName', client.name);
    setValue('customerEmail', client.email || '');
    // Formata o telefone para E.164 se necessário
    let formattedPhone = client.phone || '';
    if (formattedPhone && !formattedPhone.startsWith('+')) {
      const cleaned = formattedPhone.replace(/\D/g, '');
      if (cleaned.length === 10 || cleaned.length === 11) {
        formattedPhone = `+55${cleaned}`;
      } else if (cleaned.length > 11) {
        formattedPhone = `+${cleaned}`;
      }
    }
    setValue('customerPhone', formattedPhone);
    setValue('clientId', client.id);
    setClientSearch('');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const formatCurrencyInput = (value: string) => {
    // Remove tudo exceto números
    const numbers = value.replace(/\D/g, '');
    // Converte para número com 2 casas decimais
    const amount = parseInt(numbers || '0') / 100;
    // Formata para moeda brasileira
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCurrencyInput(e.target.value);
    const numericValue = parseFloat(formatted.replace(/[^\d,]/g, '').replace(',', '.')) || 0;
    setValue('discount', numericValue);
    e.target.value = formatted;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/app/venue-reservations')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-3xl font-bold text-brand-gold">Nova Reserva</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Venue Selection */}
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-brand-gold flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Selecionar Ambiente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <select
                value={selectedVenueId}
                onChange={(e) => setSelectedVenueId(e.target.value)}
                className="w-full p-3 bg-brand-ink border border-brand-gold/20 rounded text-brand-ivory"
                required
              >
                <option value="">Selecione um ambiente...</option>
                {venues?.map((venue) => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name} - {formatPrice(venue.defaultPrice)}/{venue.pricingType === PricingType.DAILY ? 'dia' : 'hora'}
                  </option>
                ))}
              </select>

              {selectedVenue && (
                <div className="mt-4 p-4 bg-brand-ink/50 rounded border border-brand-gold/10">
                  <div className="flex gap-4 text-sm text-brand-ivory/70">
                    {selectedVenue.capacity && <span>Capacidade: {selectedVenue.capacity} pessoas</span>}
                    {selectedVenue.area && <span>Área: {selectedVenue.area}m²</span>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Info */}
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-brand-gold flex items-center gap-2">
                <User className="w-5 h-5" />
                Dados do Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Client Search */}
              <div className="relative">
                <label className="block text-sm font-medium text-brand-ivory mb-2">Buscar Cliente (opcional)</label>
                <Input
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Digite nome, email ou telefone..."
                />
                {clientsData && clientsData.data.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-brand-ink border border-brand-gold/20 rounded-md shadow-lg max-h-60 overflow-auto">
                    {clientsData.data.map((client) => (
                      <div
                        key={client.id}
                        onClick={() => selectClient(client)}
                        className="p-3 hover:bg-brand-gold/10 cursor-pointer border-b border-brand-gold/10 last:border-0"
                      >
                        <div className="font-medium text-brand-ivory">{client.name}</div>
                        <div className="text-sm text-brand-ivory/60">{client.email || client.phone}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Nome Completo *</label>
                <Input {...register('customerName', { required: true })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Email</label>
                  <Input type="email" {...register('customerEmail')} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Telefone</label>
                  <PhoneInput
                    value={customerPhone}
                    onChange={(value) => setValue('customerPhone', value || '')}
                    defaultCountry="BR"
                    placeholder="Digite o telefone"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Date/Time Selection */}
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-brand-gold flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Período da Reserva
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Data Início *</label>
                  <Input type="date" {...register('startDate', { required: true })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">Data Fim *</label>
                  <Input type="date" {...register('endDate', { required: true })} />
                </div>
              </div>

              {selectedVenue?.pricingType === PricingType.HOURLY && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory mb-2">Hora Início</label>
                    <Input type="time" {...register('startTime')} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory mb-2">Hora Fim</label>
                    <Input type="time" {...register('endTime')} />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Desconto</label>
                <Input
                  type="text"
                  onChange={handleDiscountChange}
                  placeholder="R$ 0,00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Observações</label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="w-full p-2 bg-brand-ink border border-brand-gold/20 rounded text-brand-ivory"
                />
              </div>
            </CardContent>
          </Card>

          {/* Guests */}
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <CardHeader>
              <CardTitle className="text-brand-gold flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Convidados ({guests.length})
                </span>
                <Button type="button" size="sm" onClick={addGuest} variant="outline">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {guests.map((guest, index) => (
                <div key={index} className="p-3 bg-brand-ink/50 rounded border border-brand-gold/10">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-sm text-brand-ivory/60">Convidado {index + 1}</span>
                    <Button type="button" size="sm" variant="outline" onClick={() => removeGuest(index)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Nome"
                      value={guest.name}
                      onChange={(e) => updateGuest(index, 'name', e.target.value)}
                    />
                    <Input
                      placeholder="Email"
                      value={guest.email || ''}
                      onChange={(e) => updateGuest(index, 'email', e.target.value)}
                    />
                    <PhoneInput
                      value={guest.phone || ''}
                      onChange={(value) => updateGuest(index, 'phone', value || '')}
                      defaultCountry="BR"
                      placeholder="Telefone"
                    />
                    <Input
                      placeholder="CPF/RG"
                      value={guest.document || ''}
                      onChange={(e) => updateGuest(index, 'document', e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Price Summary */}
        <div className="space-y-6">
          <Card className="bg-brand-ink/50 border-brand-gold/20 sticky top-6">
            <CardHeader>
              <CardTitle className="text-brand-gold flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Resumo de Valores
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {calculatingPrice ? (
                <div className="text-center py-8 text-brand-ivory/50">
                  <div className="animate-spin w-6 h-6 border-2 border-brand-gold border-t-transparent rounded-full mx-auto mb-2" />
                  <p className="text-sm">Calculando...</p>
                </div>
              ) : priceCalculation ? (
                <>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-brand-ivory/70">
                      <span>Valor Base:</span>
                      <span>{formatPrice(priceCalculation.basePrice)}</span>
                    </div>
                    {priceCalculation.discount > 0 && (
                      <div className="flex justify-between text-green-400">
                        <span>Desconto:</span>
                        <span>- {formatPrice(priceCalculation.discount)}</span>
                      </div>
                    )}
                  </div>
                  <div className="pt-4 border-t border-brand-gold/20">
                    <div className="flex justify-between items-baseline">
                      <span className="text-brand-ivory/70">Total:</span>
                      <span className="text-3xl font-bold text-brand-gold">
                        {formatPrice(priceCalculation.total)}
                      </span>
                    </div>
                  </div>

                  {priceCalculation.breakdown && priceCalculation.breakdown.length > 0 && (
                    <div className="pt-4 border-t border-brand-gold/10">
                      <p className="text-sm text-brand-ivory/60 mb-2">Detalhamento:</p>
                      <div className="max-h-40 overflow-y-auto space-y-1 text-xs">
                        {priceCalculation.breakdown.map((item: any, idx: number) => (
                          <div key={idx} className="flex justify-between text-brand-ivory/60">
                            <span>{new Date(item.date).toLocaleDateString('pt-BR')}</span>
                            <span className={cn(item.isCustomPrice && 'text-brand-gold')}>
                              {formatPrice(item.price)}
                              {item.isCustomPrice && ' *'}
                            </span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-brand-ivory/40 mt-2">* Preço customizado</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-brand-ivory/50">
                  <DollarSign className="w-8 h-8 mx-auto mb-2 text-brand-gold/50" />
                  <p className="text-sm">Selecione o ambiente e as datas para calcular o valor</p>
                </div>
              )}

              <div className="pt-4 space-y-2">
                <Button type="submit" variant="premium" className="w-full" disabled={mutation.isPending || !priceCalculation}>
                  <Save className="w-4 h-4 mr-2" />
                  {mutation.isPending ? 'Salvando...' : 'Criar Reserva'}
                </Button>
                <Button type="button" variant="outline" className="w-full" onClick={() => navigate('/app/venue-reservations')}>
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
